import React from 'react';
import { useFormikContext } from 'formik';
import {
    Backdrop,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    styled,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { StepperControls, StepWrapper, StyledCardSection } from '@fnol/library/src/components';
import { useApi } from '@fnol/library/src/hooks';
import { common, getApplicationId, getIsWebsite } from '@fnol/library/src/lib';
import { getClaimConfirmation, getClaimConfirmationAnonymous } from '../../rpc/actions';
import { damageType, legalPersonType, requestType, translations } from '../../lib/consts';
import UserNotificationScreen from '../UserNotificationScreen';
import SuccessScreen from '../SuccessScreen';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

type GenericObject = { [key: string]: any };

type Props = {
    navigator: GenericObject[];
};

let data = new FormData();
const editIds = [1, 2, 3, 4, 5];

const ConfirmationScreen = ({ navigator }: Props) => {
    const { values, setErrors, setTouched, setFieldValue } = useFormikContext<any>();
    const [currentStep, { goForward, goBack }] = navigator;

    const hideStepper = () => {
        const allWithClass = Array.from(document.getElementsByClassName('MuiStepper-root'));

        allWithClass.forEach((element: Element) => {
            if (element instanceof HTMLElement) {
                element.style.display = 'none';
            }
        });
    };

    const { response: claimConfirmation, fetchMethod: fetchClaimConfirmation } = useApi(
        () => getClaimConfirmation(data),
        hideStepper,
    );

    const { response: claimConfirmationAnonymous, fetchMethod: fetchClaimConfirmationAnonymous } = useApi(
        () => getClaimConfirmationAnonymous(data),
        hideStepper,
    );

    const onCheckBoxChange = (e: any) => {
        setFieldValue('contactAgreement', e.target.checked);
    };

    const handleSubmit = (): void => {
        if (!claimConfirmation.isLoading) {
            data = new FormData();
            data.append('stetaTempId', values.tempID);
            data.append('saglasnost', values.contactAgreement);

            if (getIsWebsite(getApplicationId())) {
                setTouched(
                    {
                        oneTimePass: true,
                    },
                    false,
                );

                const existingErrors: GenericObject = {};
                if (values.oneTimePass === '' || values.oneTimePass === null) {
                    existingErrors.oneTimePass = translations.validationMessages.REQUIRED_FIELD;
                    setErrors(existingErrors);
                } else {
                    data.append('kontakt', values.tempPassContact);
                    data.append('sifra', values.oneTimePass);
                    fetchClaimConfirmationAnonymous();
                }
            } else {
                fetchClaimConfirmation();
            }
        }
    };

    const handleGoBack = (): void => {
        goBack();
    };

    const toDate = (date: string) => {
        return new Date(date);
    };

    const updDate = `${('0' + toDate(values.dateOfOccurance).getDate()).slice(-2)}.${(
        '0' +
        (toDate(values.dateOfOccurance).getMonth() + 1)
    ).slice(-2)}.${toDate(values.dateOfOccurance).getFullYear()}`;
    const updTime = `${values.hourOfOccurance}:${values.minuteOfOccurance}`;
    const updDateTime = updDate + ', ' + updTime;

    function createData(title: string, data: string) {
        return { title, data };
    }

    const basicInfoRows = [
        createData(translations.confirmationScreen.POLICY_NUMBER, `${values.policyNumber}`),
        createData(translations.TIME_OF_OCCURANCE, updDateTime),
        createData(
            translations.claimDetail.PLACE_OF_OCCURANCE,
            `${values.cityInSerbiaClaim.label !== '' ? values.cityInSerbiaClaim.label : values.city}, ${
                values.streetClaim
            }`,
        ),
        createData(
            translations.REQUEST_TYPE,
            `${
                values.requestType === requestType.MaterialDamage
                    ? translations.requestType.VEHICLE
                    : values.requestType === requestType.ImmaterialDamage
                    ? translations.requestType.IMMATERIAL
                    : values.requestType === requestType.Both
                    ? translations.requestType.BOTH
                    : ''
            }`,
        ),
        createData(
            translations.personalInfo.CLAIM_SUBMITTED_BY,
            values.submitterType === legalPersonType.individual
                ? `${values.submitterIndividualName} ${values.submitterIndividualLastName}`
                : values.submitterType === legalPersonType.legal && values.submitterLeasingEntityID
                ? `${values.submitterLegalNameForDisplay}`
                : `${values.submitterLegalEntityName}`,
        ),
        createData(
            translations.legalInfo.CONTACT_INFO,
            values.submitterType === legalPersonType.individual
                ? `${values.submitterIndividualPhone}, ${values.submitterIndividualEmail}`
                : values.submitterType === legalPersonType.legal
                ? `${values.submitterLegalEntityContactName} ${values.submitterLegalEntityContactLastName}, ${values.submitterLegalEntityContactEmail}, ${values.submitterLegalEntityContactPhone}`
                : '',
        ),
    ];

    const requestsListRows = [];

    if (
        (values.requestType === requestType.MaterialDamage || values.requestType === requestType.Both) &&
        values.damageType
    ) {
        requestsListRows.push(
            values.damageType === damageType.vehicle
                ? createData(
                      translations.confirmationScreen.VEHICLE_DAMAGE,
                      `${values.vehicleBrand} ${values.vehicleModel}, ${values.vehicleReg}`,
                  )
                : createData(
                      translations.confirmationScreen.OBJECT_DAMAGE,
                      `${values.objectType}, ${values.objectDescription}`,
                  ),
        );
    }

    if (values.requestType === requestType.ImmaterialDamage || values.requestType === requestType.Both) {
        if (
            values.submitterType === legalPersonType.individual &&
            values.submitterIndividualReportingImmaterialDamage === 'true'
        ) {
            requestsListRows.push(
                createData(
                    translations.confirmationScreen.INJURIES,
                    `${values.submitterIndividualName} ${values.submitterIndividualLastName}`,
                ),
            );
        }
        if (
            values.ownerType &&
            values.ownerType === legalPersonType.individual &&
            values.ownerIndividualReportingImmaterialDamage === 'true'
        ) {
            requestsListRows.push(
                createData(
                    translations.confirmationScreen.INJURIES,
                    `${values.ownerIndividualName} ${values.ownerIndividualLastName}`,
                ),
            );
        }
        if (values.driverType && values.driverIndividualReportingImmaterialDamage === 'true') {
            requestsListRows.push(
                createData(
                    translations.confirmationScreen.INJURIES,
                    `${values.driverIndividualName} ${values.driverIndividualLastName}`,
                ),
            );
        }
        if (
            values.userType &&
            values.userType === legalPersonType.individual &&
            values.userIndividualReportingImmaterialDamage === 'true'
        ) {
            requestsListRows.push(
                createData(
                    translations.confirmationScreen.INJURIES,
                    `${values.userIndividualName} ${values.userIndividualLastName}`,
                ),
            );
        }
        if (
            values.ownerBuildingType &&
            values.ownerBuildingType === legalPersonType.individual &&
            values.ownerBuildingIndividualReportingImmaterialDamage === 'true'
        ) {
            requestsListRows.push(
                createData(
                    translations.confirmationScreen.INJURIES,
                    `${values.ownerBuildingIndividualName} ${values.ownerBuildingIndividualLastName}`,
                ),
            );
        }

        editIds.map((editId) => {
            if (values[`injured${editId}IndividualName`]) {
                requestsListRows.push(
                    createData(
                        translations.confirmationScreen.INJURIES,
                        `${values[`injured${editId}IndividualName`]} ${values[`injured${editId}IndividualLastName`]}`,
                    ),
                );
            }
        });
    }

    return (
        <StepWrapper>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={claimConfirmation.isLoading || claimConfirmationAnonymous.isLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <StyledCardSection>
                {!claimConfirmation.isSuccess && !claimConfirmationAnonymous.isSuccess ? (
                    <>
                        <TableContainer sx={{ paddingBottom: 7 }}>
                            <Table sx={{ minWidth: 230 }} aria-label="customized table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>
                                            {translations.confirmationScreen.BASIC_INFORMATION}
                                        </StyledTableCell>
                                        <StyledTableCell align="left"></StyledTableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {basicInfoRows.map((row) => (
                                        <StyledTableRow key={row.title}>
                                            <StyledTableCell component="th" scope="row">
                                                {row.title}
                                            </StyledTableCell>
                                            <StyledTableCell align="left">{row.data}</StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                            {translations.confirmationScreen.OPENED_REQUESTS}
                        </Typography>

                        <TableContainer sx={{ paddingBottom: 7 }}>
                            <Table sx={{ minWidth: 230 }} aria-label="customized table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>{translations.confirmationScreen.REQUEST}</StyledTableCell>
                                        <StyledTableCell align="left">
                                            {translations.confirmationScreen.DESCRIPTION}
                                        </StyledTableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {requestsListRows.map((row) => (
                                        <StyledTableRow key={row.data}>
                                            <StyledTableCell width={'35%'} component="th" scope="row">
                                                {row.title}
                                            </StyledTableCell>
                                            <StyledTableCell align="left">{row.data}</StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Typography style={{ fontSize: '13px' }}>{translations.confirmationScreen.ENSUREE}</Typography>
                        <Typography style={{ fontSize: '13px' }}>
                            {translations.confirmationScreen.PRIVACY}
                            <a
                                href={`${translations.confirmationScreen.PRIVACY_POLICY_URL}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {translations.confirmationScreen.PRIVACY_POLICY_LINK}
                            </a>
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                name="contactAgreement"
                                control={<Checkbox />}
                                onChange={onCheckBoxChange}
                                label={
                                    <Typography style={{ fontSize: '13px' }}>
                                        {translations.confirmationScreen.AGREEMENT}
                                    </Typography>
                                }
                                componentsProps={{ typography: { fontSize: '13px' } }}
                            />
                        </FormGroup>

                        {getIsWebsite(getApplicationId()) && <UserNotificationScreen tempId={values.tempID} />}
                    </>
                ) : (
                    <SuccessScreen
                        claims={
                            claimConfirmation.data.data
                                ? claimConfirmation.data.data
                                : claimConfirmationAnonymous.data.data
                        }
                    />
                )}

                {claimConfirmationAnonymous.error.message && !claimConfirmationAnonymous.isSuccess && (
                    <Typography className="confirmation-message">
                        {claimConfirmation.error.response.data.match(/^\([0-9]+\)/)
                            ? translations.confirmationScreen.WRONG_PASS
                            : translations.confirmationScreen.CLAIM_ERROR}
                    </Typography>
                )}
            </StyledCardSection>

            <>
                {!claimConfirmation.isSuccess && !claimConfirmationAnonymous.isSuccess ? (
                    <StepperControls onPrevious={handleGoBack} onNext={handleSubmit} onNextLabel={common.SEND} />
                ) : null}
            </>
        </StepWrapper>
    );
};

export default ConfirmationScreen;
